import { useCallback, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	LoadTimeRating,
	type ContentMetricRow,
} from '../content-metrics/components/ContentMetricsTable.tsx';
import type { ContentType } from '../content-metrics/components/filters/TypeFilter.tsx';

import type {
	useContentMetricsQuery,
	ConfluenceContentPerformanceContentType,
	ConfluenceContentPerformanceFilters,
	ConfluenceLoadTimeRating,
} from './__generated__/useContentMetricsQuery.graphql.ts';

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

export type PaginationInfo = {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	endCursor?: string | null;
	startCursor?: string | null;
};

export type CurrentRange = {
	start: number;
	end: number;
	total: number;
};

const DEFAULT_DATE_RANGE_DAYS = 7;

// Keep default date range values stable across renders so query variables
// are deterministic and don't trigger accidental refetches.
const DEFAULT_END_TIMESTAMP = new Date().toISOString();
const DEFAULT_START_TIMESTAMP = new Date(
	Date.now() - DEFAULT_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000,
).toISOString();

const CONTENT_TYPE_TO_GQL: Record<ContentType, ConfluenceContentPerformanceContentType> = {
	page: 'PAGE',
	'live-doc': 'LIVE_PAGE',
	blog: 'BLOG_POST',
};

const RATING_TO_GQL: Record<string, ConfluenceLoadTimeRating> = {
	SLOW: 'SLOW',
	MODERATE: 'MODERATE',
	FAST: 'FAST',
};

export type ContentMetricsFilters = {
	types?: ContentType[];
	ratings?: LoadTimeRating[];
	spaceIds?: string[];
	ownerIds?: string[];
};

const buildGraphQLFilters = (
	filters?: ContentMetricsFilters,
): ConfluenceContentPerformanceFilters | undefined => {
	if (!filters) {
		return undefined;
	}

	const gqlFilters: ConfluenceContentPerformanceFilters = {};

	if (filters.types && filters.types.length > 0) {
		gqlFilters.contentType = filters.types.map((t) => CONTENT_TYPE_TO_GQL[t]);
	}

	if (filters.ratings && filters.ratings.length > 0) {
		gqlFilters.rating = filters.ratings.map((r) => RATING_TO_GQL[r]);
	}

	if (filters.spaceIds && filters.spaceIds.length > 0) {
		gqlFilters.spaceId = filters.spaceIds;
	}

	if (filters.ownerIds && filters.ownerIds.length > 0) {
		gqlFilters.ownerId = filters.ownerIds;
	}

	// Return undefined if no filters are actually set
	const hasAnyFilter =
		gqlFilters.contentType || gqlFilters.rating || gqlFilters.spaceId || gqlFilters.ownerId;
	return hasAnyFilter ? gqlFilters : undefined;
};

const mapOperations = (
	operations: ReadonlyArray<
		{ operation?: string | null; targetType?: string | null } | null | undefined
	>,
) =>
	operations.map((operation) => ({
		operation: operation?.operation ?? '',
		targetType: operation?.targetType ?? '',
	}));

const mapLoadTimeRating = (loadTimeRating?: string | null): LoadTimeRating => {
	switch (loadTimeRating) {
		case 'FAST':
			return LoadTimeRating.FAST;
		case 'SLOW':
			return LoadTimeRating.SLOW;
		default:
			return LoadTimeRating.MODERATE;
	}
};

/**
 * Fetches content metrics using Relay GraphQL query.
 *
 * @param searchQuery - Optional search term to filter content by title
 * @returns Object containing data (array of ContentMetricRow)
 *
 * This hook:
 * - Queries the confluence_contentPerformanceList GraphQL endpoint via Relay
 * - Uses last 7 days as the default time window
 * - Supports server-side search filtering via searchTerm parameter
 * - Returns paginated results (first 15 items by default)
 *
 * NOTE: The backend currently only honours the first ownerId when multiple
 * are passed in the filter array. This is a known backend bug being tracked
 * here: https://hello.jira.atlassian.cloud/browse/CCPI-245.
 * Once fixed, multi-owner filtering will work automatically.
 */
export const useContentMetrics = (searchQuery: string, filters?: ContentMetricsFilters) => {
	const { cloudId } = useSessionData();

	// Date range defaults.
	// NOTE: do not compute with `new Date()` here; see module-level constants above.
	const startTimestamp = DEFAULT_START_TIMESTAMP;
	const endTimestamp = DEFAULT_END_TIMESTAMP;

	// Pagination state: cursor stack enables back-navigation without `hasPreviousPage` cursor.
	// cursorStack holds the `after` cursors for each page visited so far (excluding current).
	// An empty string in the stack represents the "initial" (first) page with no cursor.
	const [cursorStack, setCursorStack] = useState<string[]>([]);
	const cursorStackRef = useRef<string[]>([]);
	cursorStackRef.current = cursorStack;
	const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
	const currentCursorRef = useRef<string | undefined>(undefined);
	currentCursorRef.current = currentCursor;
	const [pageSize, setPageSizeState] = useState<number>(DEFAULT_PAGE_SIZE);

	const gqlFilters = useMemo(
		() => buildGraphQLFilters(filters),
		// Use granular dependencies to avoid recomputation when the filters
		// object reference changes but the individual field values have not.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filters?.types, filters?.ratings, filters?.spaceIds, filters?.ownerIds],
	);

	const variables = useMemo(
		() => ({
			cloudId,
			first: pageSize,
			after: currentCursor,
			searchTerm: searchQuery?.trim() || undefined,
			filters: gqlFilters,
			startTimestamp,
			endTimestamp,
		}),
		[cloudId, pageSize, currentCursor, searchQuery, gqlFilters, startTimestamp, endTimestamp],
	);

	const { data, loading, error } = useQueryInterop<
		useContentMetricsQuery['response'],
		useContentMetricsQuery['variables']
	>(
		graphql`
			query useContentMetricsQuery(
				$cloudId: ID!
				$first: Int!
				$after: String
				$searchTerm: String
				$filters: ConfluenceContentPerformanceFilters
				$startTimestamp: String!
				$endTimestamp: String
			) {
				confluence_contentPerformanceList(
					cloudId: $cloudId
					first: $first
					after: $after
					searchTerm: $searchTerm
					filters: $filters
					startTimestamp: $startTimestamp
					endTimestamp: $endTimestamp
				) {
					nodes {
						id
						content {
							contentTitle
							link
							contentId
						}
						loadTimeRating
						medianLoadTime
						views
						space {
							id
							name
						}
						contentOwner {
							id
							displayName
						}
						operations {
							operation
							targetType
						}
					}
					pageInfo {
						hasNextPage
						endCursor
						startCursor
						hasPreviousPage
					}
					totalCount
				}
			}
		`,
		{
			variables,
			skip: !cloudId,
			fetchPolicy: 'network-only',
		},
	);

	const items: ContentMetricRow[] = useMemo(() => {
		const nodes = data?.confluence_contentPerformanceList?.nodes ?? [];

		return nodes.map((node) => ({
			id: node?.id ?? '',
			content: {
				contentTitle: node?.content?.contentTitle ?? '',
				link: node?.content?.link ?? '',
				contentId: node?.content?.contentId ?? '',
			},
			loadTimeRating: mapLoadTimeRating(node?.loadTimeRating),
			medianLoadTime: node?.medianLoadTime ?? 0,
			views: node?.views ?? 0,
			space: {
				id: node?.space?.id ?? '',
				name: node?.space?.name ?? '',
			},
			contentOwner: {
				id: node?.contentOwner?.id ?? '',
				displayName: node?.contentOwner?.displayName ?? '',
			},
			operations: mapOperations(node?.operations ?? []),
			actions: '',
		}));
	}, [data]);

	const spaces = useMemo(() => {
		const seen = new Map<string, string>();
		for (const item of items) {
			if (item.space.id && !seen.has(item.space.id)) {
				seen.set(item.space.id, item.space.name);
			}
		}
		return Array.from(seen, ([id, name]) => ({ id, name }));
	}, [items]);

	const rawPageInfo = data?.confluence_contentPerformanceList?.pageInfo;
	// Use previous totalCount while loading so pagination footer stays stable.
	const totalCount = data?.confluence_contentPerformanceList?.totalCount ?? null;

	const paginationInfo = useMemo<PaginationInfo | null>(
		() =>
			rawPageInfo
				? {
						hasNextPage: rawPageInfo.hasNextPage,
						hasPreviousPage: cursorStack.length > 0,
						endCursor: rawPageInfo.endCursor,
						startCursor: rawPageInfo.startCursor,
					}
				: null,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			rawPageInfo?.hasNextPage,
			rawPageInfo?.endCursor,
			rawPageInfo?.startCursor,
			cursorStack.length,
		],
	);

	const computedRange = useMemo<CurrentRange | null>(
		() =>
			totalCount !== null && items.length > 0
				? {
						start: cursorStack.length * pageSize + 1,
						end: cursorStack.length * pageSize + items.length,
						total: totalCount,
					}
				: null,
		[cursorStack.length, items.length, pageSize, totalCount],
	);

	// Preserve last known range while loading so the pagination footer stays
	// stable during page transitions rather than disappearing.
	const prevRangeRef = useRef<CurrentRange | null>(null);
	if (computedRange !== null) {
		prevRangeRef.current = computedRange;
	}
	const currentRange = loading ? prevRangeRef.current : computedRange;

	const resetPagination = useCallback(() => {
		setCursorStack([]);
		setCurrentCursor(undefined);
	}, []);

	const setPageSize = useCallback((size: number) => {
		setPageSizeState(size);
		setCursorStack([]);
		setCurrentCursor(undefined);
	}, []);

	const goToPreviousPage = useCallback(() => {
		const stack = cursorStackRef.current;
		const prevCursor = stack[stack.length - 1];
		setCursorStack(stack.slice(0, -1));
		setCurrentCursor(prevCursor === '' ? undefined : prevCursor);
	}, []);

	const goToNextPage = useCallback(() => {
		if (paginationInfo?.endCursor) {
			const snapshot = currentCursorRef.current;
			setCursorStack((prev) => [...prev, snapshot ?? '']);
			setCurrentCursor(paginationInfo.endCursor);
		}
	}, [paginationInfo?.endCursor]);

	return {
		data: items,
		spaces,
		loading,
		error,
		paginationInfo,
		totalCount,
		currentRange,
		pageSize,
		goToNextPage,
		goToPreviousPage,
		resetPagination,
		setPageSize,
	};
};
